import React, { useState, useEffect } from 'react';
import { cloudAPI } from '../../utils/api';

const EXAMPLE_QUERIES = [
    { label: 'Device profiles', sql: 'SELECT device_id, phone_manufacturer, phone_model, android_version, api_level, app_version_name, capability_score, geo_country, first_seen, last_seen FROM device_profiles ORDER BY last_seen DESC LIMIT 50' },
    { label: 'Cast sessions', sql: 'SELECT session_id, device_id, protocol, sink_name, sink_resolved_id, video_codec, audio_codec, pipeline_path, result, error_code, timestamp_start FROM cast_sessions ORDER BY timestamp_start DESC LIMIT 50' },
    { label: 'Events', sql: 'SELECT id, device_id, event_type, timestamp, properties FROM events ORDER BY timestamp DESC LIMIT 50' },
    { label: 'Table sizes', sql: "SELECT table_name, row_count, size FROM (SELECT t.table_name, COALESCE(s.n_live_tup, 0) AS row_count, pg_size_pretty(pg_total_relation_size(quote_ident(t.table_name))) AS size FROM information_schema.tables t LEFT JOIN pg_stat_user_tables s ON s.relname = t.table_name WHERE t.table_schema = 'public' AND t.table_type = 'BASE TABLE') sub ORDER BY row_count DESC" },
    { label: 'Protocol breakdown', sql: "SELECT protocol, COUNT(*) as sessions, COUNT(DISTINCT device_id) as unique_devices, ROUND(COUNT(*) FILTER (WHERE result = 'success')::numeric / NULLIF(COUNT(*), 0) * 100, 1) as success_pct FROM cast_sessions GROUP BY protocol ORDER BY sessions DESC" },
    { label: 'Top sink devices', sql: "SELECT sink_resolved_id, sink_name, COUNT(*) as sessions, COUNT(DISTINCT device_id) as unique_sources FROM cast_sessions WHERE sink_resolved_id IS NOT NULL GROUP BY sink_resolved_id, sink_name ORDER BY sessions DESC LIMIT 20" },
];

export default function DbConsolePage() {
    const [sql, setSql] = useState('SELECT * FROM device_profiles ORDER BY last_seen DESC LIMIT 20');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tables, setTables] = useState(null);
    const [selectedTable, setSelectedTable] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);

    useEffect(() => {
        cloudAPI.getTables()
            .then(res => setTables(res.data))
            .catch(() => {});
    }, []);

    const runQuery = async () => {
        if (!sql.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await cloudAPI.runQuery(sql);
            setResult(res.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            runQuery();
        }
    };

    const loadTable = async (name) => {
        setSelectedTable(name);
        setTableLoading(true);
        try {
            const res = await cloudAPI.getTable(name, 100, 0);
            setTableData(res.data);
        } catch (err) {
            setTableData(null);
        } finally {
            setTableLoading(false);
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Database Console</h2>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar — Tables */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-md p-4">
                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Tables</h3>
                        {tables?.tables?.map((t) => (
                            <button
                                key={t.table_name}
                                onClick={() => loadTable(t.table_name)}
                                className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 transition-colors ${
                                    selectedTable === t.table_name
                                        ? 'bg-zebcast-purple text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <span className="font-mono">{t.table_name}</span>
                                <span className="float-right text-xs opacity-70">{t.row_count}</span>
                            </button>
                        ))}
                        {tables?.materialized_views?.length > 0 && (
                            <>
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 mt-4">Views</h3>
                                {tables.materialized_views.map((mv) => (
                                    <button
                                        key={mv.table_name}
                                        onClick={() => loadTable(mv.table_name)}
                                        className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 transition-colors ${
                                            selectedTable === mv.table_name
                                                ? 'bg-zebcast-purple text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="font-mono text-xs">{mv.table_name}</span>
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                </div>

                {/* Main content — Console + Results */}
                <div className="lg:col-span-3 space-y-6">
                    {/* SQL Console */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-gray-900">SQL Query</h3>
                            <div className="flex gap-2">
                                <select
                                    onChange={(e) => { if (e.target.value) setSql(e.target.value); }}
                                    className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-zebcast-purple"
                                    defaultValue=""
                                >
                                    <option value="" disabled>Examples...</option>
                                    {EXAMPLE_QUERIES.map((q, i) => (
                                        <option key={i} value={q.sql}>{q.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <textarea
                            value={sql}
                            onChange={(e) => setSql(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={5}
                            className="w-full font-mono text-sm border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-zebcast-purple bg-gray-50"
                            placeholder="SELECT * FROM device_profiles LIMIT 10"
                        />
                        <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-400">Ctrl+Enter to run. Read-only queries only.</span>
                            <button
                                onClick={runQuery}
                                disabled={loading}
                                className="px-4 py-2 bg-zebcast-purple text-white rounded-md hover:bg-zebcast-dark transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Running...' : 'Run Query'}
                            </button>
                        </div>
                    </div>

                    {/* Query Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-800 font-mono">{error}</p>
                        </div>
                    )}

                    {/* Query Results */}
                    {result && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-bold text-gray-900">Results</h3>
                                <span className="text-xs text-gray-400">
                                    {result.rowCount} rows in {result.durationMs}ms
                                </span>
                            </div>
                            <ResultTable rows={result.rows} fields={result.fields} />
                        </div>
                    )}

                    {/* Table Browser */}
                    {tableData && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-bold text-gray-900">
                                    <span className="font-mono text-zebcast-purple">{tableData.table}</span>
                                    <span className="text-sm font-normal text-gray-500 ml-2">
                                        ({tableData.total} total rows, showing {tableData.rows.length})
                                    </span>
                                </h3>
                            </div>
                            {tableLoading ? (
                                <div className="text-gray-400">Loading...</div>
                            ) : (
                                <ResultTable rows={tableData.rows} fields={tableData.fields?.map(f => ({ name: f }))} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ResultTable({ rows, fields }) {
    if (!rows || rows.length === 0) {
        return <div className="text-gray-400 text-sm py-4 text-center">No rows returned</div>;
    }

    const columns = fields?.map(f => f.name || f) || Object.keys(rows[0]);

    return (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50 sticky top-0">
                    <tr>
                        {columns.map((col) => (
                            <th key={col} className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                {col}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                            {columns.map((col) => (
                                <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate font-mono">
                                    {formatCell(row[col])}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function formatCell(value) {
    if (value === null || value === undefined) return <span className="text-gray-300">NULL</span>;
    if (typeof value === 'object') return JSON.stringify(value).substring(0, 100);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    const str = String(value);
    return str.length > 100 ? str.substring(0, 100) + '...' : str;
}
